import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useWatch } from 'react-hook-form';

import { AutoNumeric } from '../../../../common/auto-numeric/AutoNumeric';
import { CommonProps } from './pricing-list/PricingItem';
import { calculateValue } from '../../../../../utils/helper-functions';
import { PricingCalculator } from '../../../../../utils/PricingCalculator';
import { useUserPermissions } from '../../../../../context/user/PermissionsProvider';
import { PoFormLabel, pricingConfig } from '../po-form-config';
import { FormPricingKeys } from './pricing-list/FormPricing';

import formStyles from '../PurchaseOrderForm.module.scss';
import { useAutoPricingState } from './context/AutoPricingProvider';
import { initialAutoPricingState } from '../../../../../constants/constants';

const { min } = pricingConfig[FormPricingKeys.MonthlyTerm].validations;

export const monthlyTermTestId = 'MonthlyTermComponent';

export const MonthlyTerm = (props: CommonProps) => {
  const { index, field, state } = props;
  const { hasAutoPricing } = useUserPermissions();
  const { autoPricings } = useAutoPricingState();
  const [value, setValue] = useState('');

  const { monthlyTerm: apiValue, weeklyRate, weeklyTerm } =
    autoPricings[state] || initialAutoPricingState;
  const [isDefaultFromPO, setIsDefaultFromPO] = useState(
    field.monthlyTerm !== ''
  );

  const weeklyTermWatched = useWatch({ name: `pricing[${index}].weeklyTerm` });
  const weeklyRentWatched = useWatch({ name: `pricing[${index}].weeklyRent` });

  const totalRtoPrice = () =>
    weeklyRentWatched &&
    weeklyTermWatched &&
    calculateValue(
      PricingCalculator.totalRtoPrice,
      weeklyRentWatched,
      weeklyTermWatched
    );

  const monthlyRent = () =>
    weeklyRentWatched &&
    calculateValue(
      PricingCalculator.monthlyRent,
      weeklyRentWatched,
      `${PricingCalculator.monthlyMultiplier()}`
    );

  const monthlyTerm = () =>
    totalRtoPrice() &&
    weeklyRentWatched &&
    calculateValue(
      PricingCalculator.monthlyTerm,
      totalRtoPrice(),
      monthlyRent()
    );

  const fieldName = `pricing[${index}].monthlyTerm`;
  useEffect(() => {
    if (
      weeklyTerm === null &&
      weeklyRate === null &&
      apiValue === null &&
      !!weeklyTermWatched &&
      !!weeklyRentWatched &&
      !isDefaultFromPO
    ) {
      //* Value to be calculated from input change
      const term =
        !!weeklyTermWatched && !!weeklyRentWatched && monthlyTerm() >= '1'
          ? monthlyTerm()
          : '';
      setValue(term);
    } else {
      setValue('');
      setIsDefaultFromPO(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weeklyTermWatched, weeklyRentWatched]);

  const imperativeValueCheck =
    !!value ||
    !!apiValue ||
    weeklyRentWatched === '' ||
    weeklyTermWatched === '';

  return useMemo(
    () => (
      <div data-testid={monthlyTermTestId}>
        <label htmlFor={fieldName}>{PoFormLabel.MonthlyTerm}:</label>
        <div className={formStyles.noFillField}>
          <Controller
            as={AutoNumeric}
            name={fieldName}
            defaultValue={field.monthlyTerm}
            {...(imperativeValueCheck && {
              imperativeValue:
                value !== '' ? value : apiValue ? apiValue.toString() : '',
              imperativeMode: true
            })}
            options={{
              minimumValue: 0,
              decimalPlaces: 0,
              ...(!hasAutoPricing && {
                maximumValue: Number.MAX_SAFE_INTEGER
              })
            }}
            placeholder="--"
            disabled
            rules={{ required: true, min }}
          />
        </div>
      </div>
    ),
    [
      fieldName,
      field.monthlyTerm,
      imperativeValueCheck,
      value,
      apiValue,
      hasAutoPricing
    ]
  );
};
